import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { useWithoutNavigation } from '~/context/OnlineContentConnectorContext';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import { GET_CASE_STUDIES_ATTEMPTS, GET_CASE_STUDY_FOR_PATHWAYS } from '~/graphql/pathways';
import {
  CaseStudy,
  CaseStudyPassedResult,
  Maybe,
  Query,
  QueryGetCaseStudiesAttemptsArgs,
  QueryGetCaseStudiesForPathwaysArgs,
} from '~/types';
import { toBoolean } from '~/utils/toBoolean';

export interface ICaseStudyWithProgress extends CaseStudy {
  passed?: boolean;
}

export interface ICaseStudyByIdMap {
  [key: string]: CaseStudy[];
}

export const formatCaseStudies = (
  caseStudies?: Maybe<CaseStudy[]>,
  attempts?: Maybe<CaseStudyPassedResult[]>,
): ICaseStudyWithProgress[] => {
  if (!caseStudies) {
    return [];
  }

  if (!attempts) {
    return caseStudies;
  }

  return caseStudies.map((study) => {
    const successAttempts = attempts.filter(
      (attempt) => Number(attempt?.case_study_id) === Number(study?.id) && attempt.passed,
    );
    const failedAttempts = attempts.filter(
      (attempt) => Number(attempt?.case_study_id) === Number(study?.id) && !attempt.passed,
    );
    const passed = successAttempts.length ? true : failedAttempts.length ? false : undefined;
    return {
      ...study,
      passed,
    };
  });
};

const useCaseStudies = (pathwaysIds: number[]) => {
  const { userInfo } = useUserInfoContext();
  const withoutNavigation = useWithoutNavigation();
  const skip = !pathwaysIds?.length || !userInfo || withoutNavigation;

  const {
    data: caseStudiesData,
    loading: loadingCaseStudiesData,
    called: calledCaseStudiesData,
  } = useQuery<Pick<Query, 'getCaseStudiesForPathways'>, QueryGetCaseStudiesForPathwaysArgs>(
    GET_CASE_STUDY_FOR_PATHWAYS,
    {
      variables: {
        ids: pathwaysIds,
      },
      skip,
    },
  );

  const {
    data: caseStudiesProgressData,
    called: calledCaseStudiesProgressData,
    loading: loadingCaseStudiesProgressData,
  } = useQuery<Pick<Query, 'getCaseStudiesAttempts'>, QueryGetCaseStudiesAttemptsArgs>(
    GET_CASE_STUDIES_ATTEMPTS,
    {
      variables: {
        pathway_ids: pathwaysIds,
      },
      skip,
      fetchPolicy: 'network-only',
    },
  );

  const caseStudiesByPathwayIdMap: ICaseStudyByIdMap = useMemo(
    () =>
      caseStudiesData?.getCaseStudiesForPathways
        ? Object.fromEntries(
            caseStudiesData?.getCaseStudiesForPathways?.map((studyDataItem) => [
              studyDataItem?.pathway?.id,
              formatCaseStudies(
                studyDataItem!.case_studies?.filter(toBoolean<CaseStudy>),
                caseStudiesProgressData?.getCaseStudiesAttempts?.filter(
                  toBoolean<CaseStudyPassedResult>,
                ),
              ),
            ]),
          )
        : {},
    [caseStudiesData, caseStudiesProgressData],
  );

  return {
    loaded:
      calledCaseStudiesData &&
      calledCaseStudiesProgressData &&
      !loadingCaseStudiesData &&
      !loadingCaseStudiesProgressData,
    loading: loadingCaseStudiesData || loadingCaseStudiesProgressData,
    caseStudiesByPathwayIdMap,
  };
};

export default useCaseStudies;
